import { useEffect, useState, useMemo } from "react";
import { Plus, Trash2, AlertTriangle, Search, Smartphone, RefreshCcw, Settings, Eye, MoreHorizontal, ChevronLeft, ChevronRight, Volume2, Play, Award, Edit, Users, Eraser, FileText, Clock, X, Music, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { deviceService, Device } from "@/services/deviceService";
import { companyService } from "@/services/companyService";
import { toast } from "sonner";
import AddDeviceDialog from "@/components/devices/AddDeviceDialog";
import DeviceDetailsDialog from "@/components/devices/DeviceDetailsDialog";
import ClearCacheDialog from "@/components/devices/ClearCacheDialog";
import BulkClearCacheDialog from "@/components/devices/BulkClearCacheDialog";
import MusicScheduleDialog from "@/components/devices/MusicScheduleDialog";
import SingleDeviceMusicScheduleDialog from "@/components/devices/SingleDeviceMusicScheduleDialog";
import BulkRestartDialog from "@/components/devices/BulkRestartDialog";
import BulkEmergencyStopDialog from "@/components/devices/BulkEmergencyStopDialog";

export default function DevicePage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showClearCacheDialog, setShowClearCacheDialog] = useState(false);
  const [showBulkClearCacheDialog, setShowBulkClearCacheDialog] = useState(false);
  const [showMusicScheduleDialog, setShowMusicScheduleDialog] = useState(false);
  const [showSingleMusicScheduleDialog, setShowSingleMusicScheduleDialog] = useState(false);
  const [showBulkRestartDialog, setShowBulkRestartDialog] = useState(false);
  const [showBulkEmergencyStopDialog, setShowBulkEmergencyStopDialog] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    loadDevices();
    loadCompanies();
  }, [currentPage]);

  // Filtre değiştiğinde ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany, selectedStatus]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const data = await deviceService.getAllDevices(currentPage, itemsPerPage);
      setDevices(data.devices || []);
      setTotalDevices(data.pagination?.total || 0);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      toast.error("Cihazlar yüklenirken hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companyService.getAllCompanies();
      setCompanies(data);
    } catch (error) {
      console.error("Firmalar yüklenirken hata:", error);
    }
  };

  // Filtrelenmiş firmalar (arama için)
  const filteredCompanies = useMemo(() => {
    if (!companySearchQuery) return companies;
    return companies.filter(c =>
      c.name.toLowerCase().includes(companySearchQuery.toLowerCase())
    );
  }, [companies, companySearchQuery]);

  // Filtrelenmiş cihazlar
  const filteredDevices = useMemo(() => {
    let result = devices;

    // Firma filtresi
    if (selectedCompany !== "all") {
      result = result.filter(d => d.company?._id === selectedCompany);
    }

    // Durum filtresi
    if (selectedStatus === "online") {
      result = result.filter(d => d.isOnline);
    } else if (selectedStatus === "offline") {
      result = result.filter(d => !d.isOnline);
    }

    // Arama filtresi
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(d =>
        d.name?.toLowerCase().includes(search) ||
        d.company?.name?.toLowerCase().includes(search) ||
        d.token?.toLowerCase().includes(search) ||
        d.location?.city?.name?.toLowerCase().includes(search) ||
        d.location?.district?.name?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [devices, selectedCompany, selectedStatus, searchTerm]);

  // İstatistikler (filtrelenmiş veriden hesapla)
  const stats = useMemo(() => {
    const total = totalDevices;
    const online = filteredDevices.filter(d => d.isOnline).length;
    const offline = filteredDevices.length - online;
    const withPlaylist = filteredDevices.filter(d => d.activePlaylist).length;

    return { total, online, offline, withPlaylist };
  }, [filteredDevices, totalDevices]);

  const handleDeleteDevice = async (deviceId: string) => {
    if (!confirm("Bu cihazı silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await deviceService.deleteDevice(deviceId);
      toast.success("Cihaz başarıyla silindi");
      loadDevices();
    } catch (error) {
      toast.error("Cihaz silinirken hata oluştu");
      console.error(error);
    }
  };

  const handleRestartDevice = async (deviceId: string) => {
    try {
      await deviceService.restartDevice(deviceId);
      toast.success("Cihaz yeniden başlatılıyor");
    } catch (error) {
      toast.error("Cihaz yeniden başlatılırken hata oluştu");
      console.error(error);
    }
  };

  const handleEmergencyStop = () => {
    const onlineDevices = devices.filter(d => d.isOnline);

    if (onlineDevices.length === 0) {
      toast.error("Acil durduracak çevrimiçi cihaz yok");
      return;
    }

    setShowBulkEmergencyStopDialog(true);
  };

  const handleRestartAll = () => {
    const onlineDevices = devices.filter(d => d.isOnline);

    if (onlineDevices.length === 0) {
      toast.error("Yeniden başlatılacak çevrimiçi cihaz yok");
      return;
    }

    setShowBulkRestartDialog(true);
  };

  const handleCancelScheduledCache = async (deviceId: string, deviceName: string) => {
    if (!confirm(`${deviceName} cihazının zamanlanmış cache temizleme işlemini iptal etmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await deviceService.cancelScheduledCacheClear(deviceId);
      toast.success("Zamanlanmış cache temizleme iptal edildi");
      loadDevices();
    } catch (error) {
      toast.error("Zamanlanmış cache temizleme iptal edilirken hata oluştu");
      console.error(error);
    }
  };

  const handleClearAllCache = () => {
    const onlineDevices = devices.filter(d => d.isOnline);

    if (onlineDevices.length === 0) {
      toast.error("Cache temizlenecek çevrimiçi cihaz yok");
      return;
    }

    // Modal'ı aç - toplu işlem için özel modal
    setShowBulkClearCacheDialog(true);
  };

  const handleMusicSchedule = () => {
    const onlineDevices = devices.filter(d => d.isOnline);

    if (onlineDevices.length === 0) {
      toast.error("Müzik zamanlaması yapılacak çevrimiçi cihaz yok");
      return;
    }

    setShowMusicScheduleDialog(true);
  };

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
        <div
          className="flex"
          style={{
            background: 'linear-gradient(to right, #dc2626, #ea580c)',
          }}
        >
          <div className="p-8 flex justify-between items-start relative w-full">
            <div className="space-y-3 flex-1">
              <div className="text-white/80 text-sm font-medium">
                Cihaz Yönetim Paneli
              </div>
              <h1 className="text-3xl font-bold text-white">
                Cihaz İzleme ve Kontrol
              </h1>
              <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                Tüm firmaların cihazlarını izleyin, durumlarını kontrol edin ve toplu
                işlemler yapın. Cihazları yeniden başlatın, cache temizleyin veya müzik
                programlayın.
              </p>
            </div>
            <div
              className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <Smartphone className="w-20 h-20 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-blue-500/10 blur-2xl"></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Toplam Cihaz</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{stats.total}</h3>
                  </div>
                  <div className="p-3.5 rounded-xl shadow-sm bg-blue-500/10">
                    <Smartphone className="w-5 h-5 text-blue-500" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 bg-blue-500"></span>
                      <span className="font-medium">Kayıtlı cihaz</span>
                    </div>
                    <span className="text-xs font-semibold text-blue-600">{stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-green-500/10 blur-2xl"></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Çevrimiçi</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{stats.online}</h3>
                  </div>
                  <div className="p-3.5 rounded-xl shadow-sm bg-green-500/10">
                    <Check className="w-5 h-5 text-green-500" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${stats.online > 0 ? "bg-green-500 animate-pulse" : "bg-slate-300"}`}></span>
                      <span className="font-medium">%{stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0} aktif</span>
                    </div>
                    <span className="text-xs font-semibold text-green-600">{stats.online}/{stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-gray-500/10 blur-2xl"></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Çevrimdışı</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{stats.offline}</h3>
                  </div>
                  <div className="p-3.5 rounded-xl shadow-sm bg-gray-500/10">
                    <X className="w-5 h-5 text-gray-500" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 bg-gray-500"></span>
                      <span className="font-medium">%{stats.total > 0 ? Math.round((stats.offline / stats.total) * 100) : 0} pasif</span>
                    </div>
                    <span className="text-xs font-semibold text-gray-600">{stats.offline}/{stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-purple-500/10 blur-2xl"></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Playlist Atanmış</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{stats.withPlaylist}</h3>
                  </div>
                  <div className="p-3.5 rounded-xl shadow-sm bg-purple-500/10">
                    <Music className="w-5 h-5 text-purple-500" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 bg-purple-500"></span>
                      <span className="font-medium">%{stats.total > 0 ? Math.round((stats.withPlaylist / stats.total) * 100) : 0} playlist</span>
                    </div>
                    <span className="text-xs font-semibold text-purple-600">{stats.withPlaylist}/{stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="space-y-4">
          {/* Arama ve Ana Butonlar */}
          <div className="flex gap-3 items-center">
            {/* Arama */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Cihaz adı, firma veya token ile arayın..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Toplu İşlemler */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-11 px-5 border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Toplu İşlemler
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleMusicSchedule}>
                  <Music className="mr-2 h-4 w-4" />
                  Planlı Çalma
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleClearAllCache}>
                  <Eraser className="mr-2 h-4 w-4" />
                  Cache Temizle
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleRestartAll}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Tümünü Yeniden Başlat
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEmergencyStop}>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Acil Durdur
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Yenile Butonu */}
            <Button
              onClick={loadDevices}
              variant="outline"
              className="h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Yenile
            </Button>

            {/* Yeni Cihaz Butonu */}
            <Button
              onClick={() => setShowAddDialog(true)}
              className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Cihaz Ekle
            </Button>
          </div>

          {/* Filtreler - Ayrı Satır */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Firma Filtresi */}
            <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={companyOpen}
                  className="w-full justify-between h-10 border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
                >
                  {selectedCompany === "all"
                    ? "Tüm Firmalar"
                    : companies.find((c) => c._id === selectedCompany)?.name || "Firma seçin"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="flex flex-col">
                  {/* Arama Input */}
                  <div className="border-b px-3 py-2">
                    <Input
                      placeholder="Firma ara..."
                      value={companySearchQuery}
                      onChange={(e) => setCompanySearchQuery(e.target.value)}
                      className="h-9"
                    />
                  </div>

                  {/* Firma Listesi */}
                  <div className="max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                    {/* Tüm Firmalar */}
                    <div
                      className={cn(
                        "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                        selectedCompany === "all" && "bg-blue-50"
                      )}
                      onClick={() => {
                        setSelectedCompany("all");
                        setCompanyOpen(false);
                        setCompanySearchQuery("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCompany === "all" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="text-sm">Tüm Firmalar</span>
                    </div>

                    {/* Filtrelenmiş Firmalar */}
                    {filteredCompanies.length === 0 ? (
                      <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                        Firma bulunamadı.
                      </div>
                    ) : (
                      filteredCompanies.map((company) => (
                        <div
                          key={company._id}
                          className={cn(
                            "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                            selectedCompany === company._id && "bg-blue-50"
                          )}
                          onClick={() => {
                            setSelectedCompany(company._id);
                            setCompanyOpen(false);
                            setCompanySearchQuery("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedCompany === company._id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="text-sm">{company.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Durum Filtresi */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="h-10 border-slate-200 rounded-lg text-sm">
                <SelectValue placeholder="Tüm Durumlar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="online">Çevrimiçi</SelectItem>
                <SelectItem value="offline">Çevrimdışı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Add Device Dialog */}
      <AddDeviceDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        companies={companies}
        onSuccess={loadDevices}
      />

      {/* Device Details Dialog */}
      <DeviceDetailsDialog
        device={selectedDevice}
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
      />

      {/* Clear Cache Dialog */}
      <ClearCacheDialog
        device={selectedDevice}
        open={showClearCacheDialog}
        onOpenChange={setShowClearCacheDialog}
        onSuccess={loadDevices}
      />

      {/* Bulk Clear Cache Dialog */}
      <BulkClearCacheDialog
        devices={devices}
        open={showBulkClearCacheDialog}
        onOpenChange={setShowBulkClearCacheDialog}
        onSuccess={loadDevices}
      />

      {/* Music Schedule Dialog */}
      <MusicScheduleDialog
        devices={devices}
        open={showMusicScheduleDialog}
        onOpenChange={setShowMusicScheduleDialog}
        onSuccess={loadDevices}
      />

      {/* Single Device Music Schedule Dialog */}
      <SingleDeviceMusicScheduleDialog
        device={selectedDevice}
        open={showSingleMusicScheduleDialog}
        onOpenChange={setShowSingleMusicScheduleDialog}
        onSuccess={loadDevices}
      />

      {/* Bulk Restart Dialog */}
      <BulkRestartDialog
        devices={devices}
        open={showBulkRestartDialog}
        onOpenChange={setShowBulkRestartDialog}
        onSuccess={loadDevices}
      />

      {/* Bulk Emergency Stop Dialog */}
      <BulkEmergencyStopDialog
        devices={devices}
        open={showBulkEmergencyStopDialog}
        onOpenChange={setShowBulkEmergencyStopDialog}
        onSuccess={loadDevices}
      />

      {/* Results Count */}
      <div className="text-sm text-slate-600">
        {(searchTerm || selectedCompany !== "all" || selectedStatus !== "all") ? (
          <>
            <span className="font-semibold text-slate-800">{filteredDevices.length}</span> cihaz bulundu
            <span className="text-slate-400"> (toplam {totalDevices})</span>
          </>
        ) : (
          <>Toplam <span className="font-semibold text-slate-800">{totalDevices}</span> cihaz</>
        )}
      </div>

      {/* Cihaz Tablosu */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Cihaz Adı</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Firma</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Lokasyon</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Grup</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Durum</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Aktif Playlist</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Zamanlanmış Cache</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm">Token</TableHead>
              <TableHead className="text-center py-4 text-slate-700 font-bold text-sm">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-24"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-32"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-28"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-20"></div></TableCell>
                    <TableCell className="py-4"><div className="h-6 bg-slate-200 rounded-lg animate-pulse w-20"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-24"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-16"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-32"></div></TableCell>
                    <TableCell className="py-4 text-right"><div className="h-8 bg-slate-200 rounded animate-pulse w-8 ml-auto"></div></TableCell>
                  </TableRow>
                ))}
              </>
            ) : filteredDevices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {(searchTerm || selectedCompany !== "all" || selectedStatus !== "all")
                    ? "Filtreye uygun cihaz bulunamadı"
                    : "Cihaz bulunamadı"}
                </TableCell>
              </TableRow>
            ) : (
              filteredDevices.map((device) => (
                <TableRow key={device._id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                  <TableCell className="py-4 font-semibold text-slate-900">{device.name}</TableCell>
                  <TableCell className="py-4 text-slate-700">{device.company?.name || "-"}</TableCell>
                  <TableCell className="py-4">
                    <div className="flex flex-col text-sm">
                      <span className="text-slate-700">{device.location?.city?.name || "-"}</span>
                      {device.location?.district?.name && (
                        <span className="text-xs text-slate-500">
                          {device.location.district.name}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-4 text-slate-700">{device.groupData?.name || "-"}</TableCell>
                  <TableCell className="py-4">
                    {device.isOnline ? (
                      <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                        <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600 animate-pulse"></div>
                        Çevrimiçi
                      </div>
                    ) : (
                      <div className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
                        <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-gray-600"></div>
                        Çevrimdışı
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-4 text-slate-700">{device.activePlaylist?.name || "-"}</TableCell>
                  <TableCell className="py-4">
                    {device.scheduledCacheClear ? (
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center rounded-lg bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
                          <Clock className="w-3.5 h-3.5 mr-1.5" />
                          <span>
                            {new Date(device.scheduledCacheClear.scheduledAt).toLocaleString("tr-TR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-lg hover:bg-red-50 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelScheduledCache(device._id, device.name);
                          }}
                          title="İptal Et"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="py-4 font-mono text-xs text-slate-600 truncate max-w-[150px]" title={device.token}>
                    {device.token}
                  </TableCell>
                  <TableCell className="text-right py-4 pr-6">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="sr-only">Menüyü aç</span>
                          <MoreHorizontal className="h-5 w-5 text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {/* Duraklat/Çal */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Play/Pause fonksiyonu eklenecek (WebSocket gerekli)
                            toast.info("Çalma/Duraklat özelliği yakında eklenecek");
                          }}
                          disabled={!device.isOnline}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Çal
                          {device.activePlaylist?.name && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({device.activePlaylist.name})
                            </span>
                          )}
                        </DropdownMenuItem>

                        {/* Ses Ayarları */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Ses ayarları dialog'u eklenecek
                            toast.info("Ses ayarları özelliği yakında eklenecek");
                          }}
                          disabled={!device.isOnline}
                        >
                          <Volume2 className="mr-2 h-4 w-4" />
                          Ses Ayarları
                        </DropdownMenuItem>

                        {/* Planlı Çalma */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDevice(device);
                            setShowSingleMusicScheduleDialog(true);
                          }}
                          disabled={!device.isOnline}
                        >
                          <Music className="mr-2 h-4 w-4" />
                          Planlı Çalma
                        </DropdownMenuItem>

                        {/* Detaylar */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDevice(device);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Detaylar
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        {/* Yeniden Başlat */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestartDevice(device._id);
                          }}
                          disabled={!device.isOnline}
                        >
                          <RefreshCcw className="mr-2 h-4 w-4" />
                          Yeniden Başlat
                        </DropdownMenuItem>

                        {/* Cache Temizle */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDevice(device);
                            setShowClearCacheDialog(true);
                          }}
                          disabled={!device.isOnline}
                        >
                          <Eraser className="mr-2 h-4 w-4" />
                          Cache Temizle
                        </DropdownMenuItem>

                        {/* Logları Getir */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Log getirme API'si eklenecek
                            toast.info("Logları getirme özelliği yakında eklenecek");
                          }}
                          disabled={!device.isOnline}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Logları Getir
                        </DropdownMenuItem>

                        {/* Sertifika Oluştur */}
                        {!device.hasCertificate && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Sertifika oluşturma API'si eklenecek
                              toast.info("Sertifika oluşturma özelliği yakında eklenecek");
                            }}
                          >
                            <Award className="mr-2 h-4 w-4" />
                            Sertifika Oluştur
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />

                        {/* Düzenle */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Düzenleme dialog'u eklenecek
                            toast.info("Düzenleme özelliği yakında eklenecek");
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Düzenle
                        </DropdownMenuItem>

                        {/* Gruba Ekle/Gruptan Çıkar */}
                        {device.groupData ? (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Gruptan çıkarma API'si eklenecek
                              toast.info("Gruptan çıkarma özelliği yakında eklenecek");
                            }}
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Gruptan Çıkar
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Gruba ekleme dialog'u eklenecek
                              toast.info("Gruba ekleme özelliği yakında eklenecek");
                            }}
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Gruba Ekle
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />

                        {/* Acil Durdur */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`${device.name} cihazını acil durdurmak istediğinizden emin misiniz?`)) {
                              deviceService.emergencyStop([device._id]);
                              toast.success("Acil durdurma komutu gönderildi");
                            }
                          }}
                          disabled={!device.isOnline}
                        >
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          Acil Durdur
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        {/* Sil */}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDevice(device._id);
                          }}
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Sil
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{totalDevices}</span> cihaz içinden{" "}
            <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
            <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, totalDevices)}</span>{" "}
            arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Önceki sayfa"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Önceki</span>
            </Button>

            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let startPage = Math.max(1, currentPage - 2);
                if (currentPage > totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                }

                const page = startPage + i;
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`h-10 min-w-10 px-3 rounded-lg ${
                        currentPage === page
                          ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Sonraki sayfa"
            >
              <span className="hidden sm:inline">Sonraki</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
